import HistoryIcon from '@mui/icons-material/History';
import HouseIcon from '@mui/icons-material/House';
import MailIcon from '@mui/icons-material/Mail';
import PasswordRoundedIcon from '@mui/icons-material/PasswordRounded';
import PhoneIcon from '@mui/icons-material/Phone';
import PinRoundedIcon from '@mui/icons-material/PinRounded';
import QuizRoundedIcon from '@mui/icons-material/QuizRounded';
import { Avatar, Box, Button, Card, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { InfoCard } from '../../components/InfoCard';
import { MyProfileData } from "./MyProfileData";
import { iconStyle, myProfileStyle, nameCardAvatarStyle, nameCardStyle, securityCardRowStyle, securityCardStyle } from './styled';
const NO_RESTRICTIONS = "No Current Suspensions or Restrictions";

const NameCard = ({ name, lua }: {
  name?: string,
  lua?: string
}) => (<Card sx={nameCardStyle}>
  <Avatar sx={nameCardAvatarStyle} />
  <Typography variant={"h5"} color={"primary.contrastText"}>{name}</Typography>
  <Typography variant={"overline"} color={"primary.contrastText"}>{lua}</Typography>
</Card>);

const SecurityCardRow = ({ text, onUpdate, icon, dataAutomation }: {
  text: string,
  onUpdate: () => void,
  icon: JSX.Element,
  dataAutomation?: { button?: string }
}) => (<Box sx={securityCardRowStyle}>
  {icon}
  <Button
    data-automation={dataAutomation?.button || 'SecurityCardRowButton'}
    variant={"text"}
    onClick={onUpdate}
  >
    {text}
  </Button>
</Box>)

const ContactInfoCardRow = ({ icon, infoType, info, hideUpdateButton = false, onUpdate, dataAutomation }: {
  icon: JSX.Element,
  infoType: string,
  info?: string | string[],
  hideUpdateButton?: boolean,
  onUpdate?: () => void,
  dataAutomation?: { info?: string, infoType?: string, icon?: string, button?: string }
}) => {
  const renderInfo = (data: string | undefined, key: number) => (
    <Typography key={key} data-automation={dataAutomation?.info || 'info'} >{data}</Typography>
  )

  return (
    <>
      <Box sx={{
        display: "flex",
        gap: 1,
        padding: 1
      }}>
        {icon}
        <div>
          <Typography
            data-automation={dataAutomation?.infoType || 'infoType'}
            sx={{ fontWeight: "bold" }}
          >
            {infoType}
          </Typography>
          {info && Array.isArray(info) ?
            info.map((data, i) => renderInfo(data, i)) : renderInfo(info, 0)
          }
        </div>
      </Box>
      <Box
        sx={{ textAlign: "end" }}
        hidden={hideUpdateButton}
      >
        <Button
          data-automation={dataAutomation?.button || 'infoButton'}
          variant={"text"}
          onClick={onUpdate}
        >Update</Button>
      </Box>
    </>
  )
};

const SummaryInfo = ({ title = "Current", value, onSeeMore, dataAutomation }: {
  title?: string,
  value?: string | string[],
  onSeeMore?: () => void | undefined
  dataAutomation?: {
    title?: string,
    seeMore?: string,
    data?: string,
  }
}) => (<>
  <Typography
    data-automation={dataAutomation?.title ?? 'title'}
    sx={{ fontWeight: "bold" }}
  >
    {title}
  </Typography>
  <div data-automation={dataAutomation?.data ?? 'data'}>
    {
      Array.isArray(value)
        ? value.map((s, i) => (<Typography key={i}>{s}</Typography>))
        : <Typography>{value}</Typography>
    }
  </div>
  {onSeeMore && <Box sx={{ textAlign: "end" }}>
    <Button
      data-automation={dataAutomation?.seeMore ?? 'seeMore'}
      variant={"text"}
      onClick={onSeeMore}
    >
      See More
    </Button>
  </Box>}
</>);

export const MyProfile = ({
  data,
  onUpdateSmbPassword,
  onUpdateIvrPassword,
  onUpdateSecurityQuestion,
  onUpdateAddress,
  onUpdatePhone,
  onUpdateEmail,
  onSeeMoreRatings,
  onSeeMoreCapabilities,
  onSeeMoreSuspensionsRestrictions,
  onSeeMoreTrades,
  onSeeMoreTickets,
  onViewMyUsageHistory
}: {
  data: MyProfileData,
  onUpdateSmbPassword: () => void,
  onUpdateIvrPassword: () => void,
  onUpdateSecurityQuestion: () => void,
  onUpdateAddress: () => void,
  onUpdatePhone: () => void,
  onUpdateEmail: () => void,
  onSeeMoreRatings: () => void,
  onSeeMoreCapabilities: () => void,
  onSeeMoreSuspensionsRestrictions: () => void,
  onSeeMoreTrades: () => void,
  onSeeMoreTickets: () => void,
  onViewMyUsageHistory: () => void,
}) => {
  return <Grid container>
    {/** This \<Grid item> acts as an offset to keep the item below centered */}
    <Grid item xs={0} sm={0} md={2} lg={3} xl={2} />
    <Grid item xs={12} sm={12} md={8} lg={6} xl={8} sx={myProfileStyle}>
      <NameCard {...data} />
      <InfoCard dataAutomation={{ header: "ProfileSecurityPaneHeaderText" }} header={"Profile Security"}>
        <Box sx={securityCardStyle}>
          <SecurityCardRow
            icon={<PasswordRoundedIcon sx={iconStyle} />}
            onUpdate={onUpdateSmbPassword}
            text={"Update MYBCMEA Password"}
            dataAutomation={{ button: "UpdateSMBPasswordButton" }}
          />
          {data.hasIvrProfile && <SecurityCardRow
            icon={<PinRoundedIcon sx={iconStyle} />}
            onUpdate={onUpdateIvrPassword}
            text={"Update IVR Password"}
            dataAutomation={{ button: "UpdateIVRPasswordButton" }}
          />}
          <SecurityCardRow
            icon={<QuizRoundedIcon sx={iconStyle} />}
            onUpdate={onUpdateSecurityQuestion}
            text={"Update Security Question"}
            dataAutomation={{ button: "UpdateSecurityQuestionButton" }}
          />
          <SecurityCardRow
            icon={<HistoryIcon sx={iconStyle} />}
            onUpdate={onViewMyUsageHistory}
            text={"View My Usage History"}
          />
        </Box>
      </InfoCard>
      <InfoCard dataAutomation={{ header: "ContactHeaderPaneText" }} header={"Contact"}>
        <ContactInfoCardRow
          icon={<HouseIcon sx={iconStyle} />}
          infoType={"Address"}
          info={data.address}
          hideUpdateButton
          onUpdate={onUpdateAddress}
          dataAutomation={{
            infoType: "ContactSubHeaderPaneAddresslabel",
            info: "UserAddress",
            button: "UpdateAddressButton"
          }}
        />
        <ContactInfoCardRow
          icon={<PhoneIcon sx={iconStyle} />}
          infoType={"Phone"}
          info={data.phones}
          hideUpdateButton
          onUpdate={onUpdatePhone}
          dataAutomation={{
            infoType: "ContactSubHeaderPanePhonelabel",
            info: "UserPhoneNumber",
            button: "UpdatePhoneButton"
          }}
        />
        <ContactInfoCardRow
          icon={<MailIcon sx={iconStyle} />}
          infoType={"Email"}
          info={data.email}
          onUpdate={onUpdateEmail}
          dataAutomation={{
            infoType: "ContactSubHeaderPaneEmaillabel",
            button: "UpdateEmailButton"
          }}
        />
      </InfoCard>
      <InfoCard
        dataAutomation={{ header: "BoardAndPlatePaneHeaderText" }}
        header={"Board & Plate"}
      >
        <SummaryInfo
          dataAutomation={{
            title: "BoardAndPlatePaneSubHeaderText",
            data: "BoardAndPlatePaneValueText"
          }}
          title="Vancouver Board and Plate"
          value={data.boardPlate.length === 0 ? "No Current Plate" : data.boardPlate}
        />
      </InfoCard>
      <InfoCard dataAutomation={{ header: "RatingsPaneHeaderText" }} header={"Ratings"}>
        <SummaryInfo
          dataAutomation={{ title: "RatingsCurrentHeader", data: "RatingsData", seeMore: "RatingsSeeMore" }}
          value={data.currentRatings === "" ? "No Current Ratings" : data.currentRatings}
          onSeeMore={onSeeMoreRatings} />
      </InfoCard>
      <InfoCard dataAutomation={{ header: "CapabilitiesPaneHeaderText" }} header={"Capabilities"}>
        <SummaryInfo
          value={data.currentCapabilities === "" ? "No Current Capabilities" : data.currentCapabilities}
          onSeeMore={onSeeMoreCapabilities} />
      </InfoCard>
      <InfoCard dataAutomation={{ header: 'TradesPaneHeaderText' }} header={"Trades"}>
        <SummaryInfo
          dataAutomation={{
            data: 'TradeData',
            seeMore: 'TradeSeeMore'
          }}
          value={data.currentTrades === "" ? "No Current Trades" : data.currentTrades}
          onSeeMore={onSeeMoreTrades} />
      </InfoCard>
      <InfoCard dataAutomation={{ header: "TicktesPaneHeaderText" }} header={"Tickets"}>
        <SummaryInfo
          dataAutomation={{
            data: 'TicketsData',
          }}
          value={data.tickets === "" ? "No Current Tickets" : data.tickets}
          onSeeMore={onSeeMoreTickets} />
      </InfoCard>
      <InfoCard
        dataAutomation={{ header: 'SusAndReSPaneHeaderText' }}
        header={"Suspensions & Restrictions"}
      >
        <SummaryInfo
          dataAutomation={{
            title: 'SuspensionMostRecentHeader',
            data: data.currentSuspRest.length === 0 ? 'SuspensionNoData' : 'SuspensionData',
            seeMore: 'SuspensionSeeMore'
          }}
          title="Active"
          value={data.currentSuspRest.length === 0 ? NO_RESTRICTIONS : data.currentSuspRest}
          onSeeMore={onSeeMoreSuspensionsRestrictions}
        />
      </InfoCard>
    </Grid>
  </Grid>;
}